
import { useState, useEffect, useRef, Suspense } from "react";
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from "@/app/components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import {
  CostingSummary, EventHeader, ExpensesSection, KeyMetrics,
  PnLSummary, ReportData, RevenueSection, VendorAllocations
} from "./components";
import { fmt, fmtDate, statusColors } from "./components/types";
import { formatDate, formatDateTime } from "@/lib/dateFormat";
import { BarChart3, DollarSign, Package, Receipt, TrendingUp, Printer } from "lucide-react";

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-20 bg-stone-100 rounded-2xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-stone-100 rounded-2xl" />)}
      </div>
      <div className="h-48 bg-stone-100 rounded-2xl" />
      <div className="h-40 bg-stone-100 rounded-2xl" />
    </div>
  );
}

// ─── Print Report ─────────────────────────────────────────────────────────────
// Renders every section of the event report in a clean, print-only layout.
// Hidden on screen; the print CSS makes only this visible during window.print().
function PrintReport({ data }: { data: ReportData }) {
  const { event, financials: f, vendor_detail, vendor_totals, service_totals, service_detail, client_payments, expenses, is_projected } = data;
  const profit = f.profit ?? 0;
  const totalCosts = service_totals.total_cost + vendor_totals.total_assigned + f.adhoc_cost;
  const profitMargin = f.net_revenue > 0 ? ((profit / f.net_revenue) * 100).toFixed(1) : "0.0";

  const th = "text-left px-3 py-2 text-xs font-semibold text-stone-500 uppercase border-b border-stone-200 bg-stone-50";
  const td = "px-3 py-2 text-xs text-stone-700 border-b border-stone-100";
  const tdr = "px-3 py-2 text-xs text-stone-700 border-b border-stone-100 text-right";

  return (
    <div id="print-report" style={{ display: "none", fontFamily: "system-ui, sans-serif", color: "#1c1917", background: "#fff", padding: "24px", maxWidth: "900px", margin: "0 auto" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, borderBottom: "2px solid #e7e5e4", paddingBottom: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#92400e", letterSpacing: "-0.5px" }}>Shalimar Events</div>
          <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>Event Performance Report</div>
        </div>
        <div style={{ textAlign: "right", fontSize: 11, color: "#78716c" }}>
          <div>Printed: {formatDate(new Date())}</div>
          {is_projected && <div style={{ color: "#d97706", fontWeight: 600, marginTop: 4 }}>⚠ Projected figures</div>}
        </div>
      </div>

      {/* ── Event Info ── */}
      <div style={{ background: "#fafaf9", border: "1px solid #e7e5e4", borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1c1917" }}>{event.name}</div>
            <div style={{ fontSize: 12, color: "#78716c", marginTop: 4 }}>
              {event.client && <span style={{ fontWeight: 600, color: "#44403c" }}>{event.client}</span>}
              {event.event_type && <span style={{ color: "#a8a29e" }}> · {event.event_type}</span>}
              {event.status && (
                <span style={{ marginLeft: 8, background: "#f5f5f4", border: "1px solid #e7e5e4", borderRadius: 12, padding: "2px 8px", fontSize: 10, fontWeight: 600, textTransform: "capitalize" }}>
                  {event.status.replace(/_/g, " ")}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 24, fontSize: 11, color: "#78716c" }}>
            {event.start_date && <div><div style={{ fontWeight: 600 }}>Start Date</div><div style={{ color: "#44403c" }}>{fmtDate(event.start_date)}</div></div>}
            {event.end_date && <div><div style={{ fontWeight: 600 }}>End Date</div><div style={{ color: "#44403c" }}>{fmtDate(event.end_date)}</div></div>}
            {event.guest_count > 0 && <div><div style={{ fontWeight: 600 }}>Guests</div><div style={{ color: "#44403c" }}>{event.guest_count}</div></div>}
          </div>
        </div>
      </div>

      {/* ── Key Metrics ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total Revenue", value: fmt(f.net_revenue), sub: "From Client", color: "#059669" },
          { label: "Total Costs", value: fmt(totalCosts), sub: `${Math.round(totalCosts / Math.max(f.net_revenue, 1) * 100)}% of revenue`, color: "#e11d48" },
          { label: profit >= 0 ? "Net Profit" : "Net Loss", value: fmt(Math.abs(profit)), sub: `${profitMargin}% margin`, color: profit >= 0 ? "#059669" : "#e11d48" },
          { label: "Client Balance Due", value: fmt(f.customer?.balance ?? 0), sub: `Paid: ${fmt(f.customer?.paid ?? 0)}`, color: (f.customer?.balance ?? 0) > 0 ? "#d97706" : "#059669" },
        ].map(m => (
          <div key={m.label} style={{ border: "1px solid #e7e5e4", borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 10, color: "#78716c", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: m.color }}>{m.value}</div>
            <div style={{ fontSize: 10, color: "#a8a29e", marginTop: 2 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Revenue & Client Payments ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1917", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #e7e5e4" }}>
          Revenue & Client Payments
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 12 }}>
          {[
            { label: "Invoice Total", value: fmt(f.income_subtotal) },
            { label: "Discount", value: `– ${fmt(f.discount_amount)}` },
            { label: "Net Revenue", value: fmt(f.net_revenue) },
            { label: "Client Paid", value: fmt(f.customer?.paid ?? 0) },
            { label: "Balance Due", value: fmt(f.customer?.balance ?? 0) },
          ].map(item => (
            <div key={item.label} style={{ background: "#fafaf9", border: "1px solid #e7e5e4", borderRadius: 6, padding: "8px 10px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#1c1917" }}>{item.value}</div>
              <div style={{ fontSize: 10, color: "#a8a29e", marginTop: 2 }}>{item.label}</div>
            </div>
          ))}
        </div>
        {client_payments.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>
                {["Date", "Method", "Reference", "Notes", "Amount"].map(h => (
                  <th key={h} className={th} style={{ textAlign: h === "Amount" ? "right" : "left", padding: "6px 10px", fontSize: 10, fontWeight: 600, color: "#78716c", background: "#fafaf9", borderBottom: "1px solid #e7e5e4" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {client_payments.map((p, i) => (
                <tr key={i}>
                  <td style={{ padding: "5px 10px", borderBottom: "1px solid #f5f5f4", fontSize: 11 }}>{fmtDate(p.payment_date)}</td>
                  <td style={{ padding: "5px 10px", borderBottom: "1px solid #f5f5f4", fontSize: 11, textTransform: "capitalize" }}>{p.payment_method ?? "—"}</td>
                  <td style={{ padding: "5px 10px", borderBottom: "1px solid #f5f5f4", fontSize: 11, color: "#78716c" }}>{p.reference_number ?? "—"}</td>
                  <td style={{ padding: "5px 10px", borderBottom: "1px solid #f5f5f4", fontSize: 11, color: "#78716c" }}>{p.notes ?? "—"}</td>
                  <td style={{ padding: "5px 10px", borderBottom: "1px solid #f5f5f4", fontSize: 11, fontWeight: 700, color: "#059669", textAlign: "right" }}>{fmt(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Item Allocations ── */}
      {(vendor_detail.length > 0 || service_detail.length > 0) && (
        <div style={{ marginBottom: 20, pageBreakInside: "avoid" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1917", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #e7e5e4" }}>
            Item Allocations
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#fafaf9", borderBottom: "1px solid #e7e5e4" }}>
                {["Type", "Item / Vendor", "Qty", "Billed", "Cost", "Profit / Balance", "Status"].map((h, i) => (
                  <th key={h} style={{ padding: "6px 10px", fontSize: 10, fontWeight: 600, color: "#78716c", textAlign: i >= 2 ? "right" : "left", borderBottom: "1px solid #e7e5e4" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {service_detail.map((s, i) => {
                const profit = s.revenue - s.cost;
                return (
                  <tr key={`s${i}`} style={{ borderBottom: "1px solid #f5f5f4" }}>
                    <td style={{ padding: "5px 10px" }}><span style={{ background: "#ede9fe", color: "#7c3aed", borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 600 }}>SVC</span></td>
                    <td style={{ padding: "5px 10px", fontWeight: 500 }}>{s.name}</td>
                    <td style={{ padding: "5px 10px", textAlign: "right" }}>{s.quantity}</td>
                    <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(s.revenue)}</td>
                    <td style={{ padding: "5px 10px", textAlign: "right", color: "#e11d48" }}>{fmt(s.cost)}</td>
                    <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600, color: profit >= 0 ? "#059669" : "#e11d48" }}>{fmt(profit)}</td>
                    <td style={{ padding: "5px 10px", textAlign: "right", fontSize: 10, color: "#059669" }}>Active</td>
                  </tr>
                );
              })}
              {vendor_detail.map((v, i) => (
                <tr key={`v${i}`} style={{ borderBottom: "1px solid #f5f5f4" }}>
                  <td style={{ padding: "5px 10px" }}><span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 600 }}>VND</span></td>
                  <td style={{ padding: "5px 10px" }}>
                    <div style={{ fontWeight: 500 }}>{v.vendor_name}</div>
                    {v.description && <div style={{ fontSize: 10, color: "#a8a29e" }}>{v.description}</div>}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "right" }}>—</td>
                  <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(v.assigned)}</td>
                  <td style={{ padding: "5px 10px", textAlign: "right", color: "#e11d48" }}>{fmt(v.total_amount)}</td>
                  <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600, color: v.balance > 0 ? "#d97706" : "#059669" }}>{fmt(v.balance)}</td>
                  <td style={{ padding: "5px 10px", textAlign: "right", fontSize: 10, textTransform: "capitalize" }}>{v.status}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#fafaf9", borderTop: "2px solid #e7e5e4", fontWeight: 700 }}>
                <td colSpan={3} style={{ padding: "6px 10px", fontSize: 11 }}>Totals</td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 11 }}>
                  {fmt(service_detail.reduce((s, r) => s + r.revenue, 0) + vendor_detail.reduce((s, r) => s + r.assigned, 0))}
                </td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 11, color: "#e11d48" }}>
                  {fmt(service_detail.reduce((s, r) => s + r.cost, 0) + vendor_detail.reduce((s, r) => s + r.total_amount, 0))}
                </td>
                <td colSpan={2} style={{ padding: "6px 10px", textAlign: "right", fontSize: 11, color: "#059669" }}>
                  {fmt(service_detail.reduce((s, r) => s + (r.revenue - r.cost), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ── Additional Expenses ── */}
      {expenses.length > 0 && (
        <div style={{ marginBottom: 20, pageBreakInside: "avoid" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1917", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #e7e5e4" }}>
            Additional Expenses
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#fafaf9" }}>
                {["Title", "Category", "Date", "Paid To", "Amount"].map((h, i) => (
                  <th key={h} style={{ padding: "6px 10px", fontSize: 10, fontWeight: 600, color: "#78716c", textAlign: i === 4 ? "right" : "left", borderBottom: "1px solid #e7e5e4" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expenses.map((e, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f5f5f4" }}>
                  <td style={{ padding: "5px 10px", fontWeight: 500 }}>{e.title}</td>
                  <td style={{ padding: "5px 10px", color: "#78716c" }}>{e.category}</td>
                  <td style={{ padding: "5px 10px", color: "#78716c" }}>{fmtDate(e.expense_date)}</td>
                  <td style={{ padding: "5px 10px", color: "#78716c" }}>{e.paid_to ?? "—"}</td>
                  <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(e.amount)}</td>
                </tr>
              ))}
              <tr style={{ background: "#fafaf9", borderTop: "2px solid #e7e5e4", fontWeight: 700 }}>
                <td colSpan={4} style={{ padding: "6px 10px", fontSize: 11 }}>Total</td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 11 }}>{fmt(expenses.reduce((s, e) => s + e.amount, 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ── P&L Summary ── */}
      <div style={{ pageBreakInside: "avoid" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1917", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #e7e5e4" }}>
          Profit & Loss Summary
        </div>
        <table style={{ width: "100%", maxWidth: 420, borderCollapse: "collapse", fontSize: 11 }}>
          <tbody>
            {[
              { label: "Net Revenue", value: fmt(f.net_revenue), bold: false },
              { label: "Service Cost", value: `– ${fmt(service_totals.total_cost)}`, bold: false, indent: true },
              { label: "Vendor Cost", value: `– ${fmt(vendor_totals.total_assigned)}`, bold: false, indent: true },
              { label: "Additional Expenses", value: `– ${fmt(f.adhoc_cost)}`, bold: false, indent: true },
              null,
              { label: profit >= 0 ? "Gross Profit" : "Net Loss", value: fmt(Math.abs(profit)), bold: true, color: profit >= 0 ? "#059669" : "#e11d48" },
              { label: "Profit Margin", value: `${profitMargin}%`, bold: false, color: profit >= 0 ? "#059669" : "#e11d48", indent: true },
            ].map((row, i) => {
              if (row === null) return <tr key={i}><td colSpan={2} style={{ padding: "4px 0", borderBottom: "1px solid #e7e5e4" }} /></tr>;
              const r = row as any;
              return (
                <tr key={i}>
                  <td style={{ padding: "5px 0", paddingLeft: r.indent ? 16 : 0, fontWeight: r.bold ? 700 : 400, color: r.color ?? "#1c1917", fontSize: r.bold ? 13 : 11 }}>
                    {r.label}
                  </td>
                  <td style={{ padding: "5px 0", textAlign: "right", fontWeight: r.bold ? 700 : 400, color: r.color ?? "#1c1917", fontSize: r.bold ? 13 : 11 }}>
                    {r.value}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Footer ── */}
      <div style={{ marginTop: 32, paddingTop: 12, borderTop: "1px solid #e7e5e4", fontSize: 10, color: "#a8a29e", display: "flex", justifyContent: "space-between" }}>
        <span>Shalimar Events — Confidential</span>
        <span>Generated on {formatDateTime(new Date())}</span>
      </div>
    </div>
  );
}

// ─── Tab Nav ──────────────────────────────────────────────────────────────────
type Tab = "overview" | "financial" | "vendors" | "expenses" | "profitability";

function TabNavigation({ activeTab, onTabChange, tabs }: {
  activeTab: Tab;
  onTabChange: (t: Tab) => void;
  tabs: { id: Tab; label: string; icon: React.ReactNode }[];
}) {
  return (
    <div className="bg-white border-b border-stone-200 rounded-t-xl overflow-x-auto">
      <div className="flex gap-1 p-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-amber-100 text-amber-700 border border-amber-300"
                : "text-stone-600 hover:bg-stone-50 border border-transparent"
            }`}
          >
            <span className="w-4 h-4">{tab.icon}</span>
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function EventDetailReportPageContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = Number(searchParams.get("id"));
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    reportsApi
      .eventDetailReport(eventId)
      .then((res: any) => {
        if (res?.success) setData(res.data);
        else setError("Failed to load report.");
      })
      .catch(() => setError("Failed to load report."))
      .finally(() => setLoading(false));
  }, [eventId]);

  const handlePrint = () => {
    // Make print div visible then inject CSS to hide everything else
    const printEl = document.getElementById("print-report");
    if (printEl) printEl.style.display = "block";

    const style = document.createElement("style");
    style.id = "print-override-css";
    style.innerHTML = `
      @media print {
        body * { visibility: hidden !important; }
        #print-report, #print-report * { visibility: visible !important; }
        #print-report {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          background: white !important;
          z-index: 99999 !important;
        }
        @page { margin: 1cm; size: A4 portrait; }
      }
    `;
    document.head.appendChild(style);
    styleRef.current = style;

    window.onafterprint = () => {
      if (styleRef.current) {
        document.head.removeChild(styleRef.current);
        styleRef.current = null;
      }
      if (printEl) printEl.style.display = "none";
      window.onafterprint = null;
    };

    window.print();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Skeleton />
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700 text-sm">
          {error ?? "No data available."}
        </div>
      </DashboardLayout>
    );
  }

  const { event, financials, vendor_detail, vendor_totals, service_totals, service_detail, client_payments, expenses, is_projected } = data;

  const tabs = [
    { id: "overview" as Tab,      label: "Overview",     icon: <BarChart3 className="w-4 h-4" /> },
    { id: "financial" as Tab,     label: "Financial",    icon: <DollarSign className="w-4 h-4" /> },
    { id: "vendors" as Tab,       label: "Vendors",      icon: <Package className="w-4 h-4" /> },
    { id: "expenses" as Tab,      label: "Expenses",     icon: <Receipt className="w-4 h-4" /> },
    ...(!isStaff ? [{ id: "profitability" as Tab, label: "P&L", icon: <TrendingUp className="w-4 h-4" /> }] : []),
  ];

  return (
    <DashboardLayout>
      {/* The hidden print view — rendered once, made visible during window.print() */}
      <PrintReport data={data} />

      <div className="flex flex-col gap-4 sm:gap-5">
        {/* Header row */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Reports
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 text-sm font-semibold text-white px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print Report
          </button>
        </div>

        {/* Event Header */}
        <EventHeader event={event} isProjected={is_projected} />

        {/* Key Metrics */}
        <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4 shadow-sm">
          <KeyMetrics financials={financials} vendorTotals={vendor_totals} serviceTotals={service_totals} />
        </div>

        {/* Tabbed Content */}
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm">
          <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs} />
          <div className="p-4 sm:p-6">
            {activeTab === "overview" && (
              <div className="space-y-4">
                <RevenueSection financials={financials} clientPayments={client_payments} />
                <VendorAllocations vendorDetail={vendor_detail} serviceDetail={service_detail} />
              </div>
            )}
            {activeTab === "financial" && (
              <div className="space-y-4">
                <RevenueSection financials={financials} clientPayments={client_payments} />
                <CostingSummary financials={financials} vendorTotals={vendor_totals} serviceTotals={service_totals} expenses={expenses} />
              </div>
            )}
            {activeTab === "vendors" && (
              <VendorAllocations vendorDetail={vendor_detail} serviceDetail={service_detail} />
            )}
            {activeTab === "expenses" && (
              <ExpensesSection expenses={expenses} />
            )}
            {activeTab === "profitability" && !isStaff && (
              <PnLSummary financials={financials} vendorTotals={vendor_totals} serviceTotals={service_totals} />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default function EventDetailReportPage() {
  return (
    <Suspense fallback={<DashboardLayout><Skeleton /></DashboardLayout>}>
      <EventDetailReportPageContent />
    </Suspense>
  );
}
